import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Trash2, ChevronDown, ChevronRight, ArrowLeft, Clock, User as UserIcon, FileWarning } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
};

const LLMArtifacts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [detail, setDetail] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/llm-artifacts`);
      setSnapshots(data.snapshots || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (userId) => {
    const next = { ...expanded, [userId]: !expanded[userId] };
    setExpanded(next);
    if (next[userId] && !detail[userId]) {
      try {
        const { data } = await axios.get(`${API}/admin/llm-artifacts/${userId}`);
        setDetail((d) => ({ ...d, [userId]: data }));
      } catch (err) {
        toast.error('Détail indisponible');
      }
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Purger ce snapshot ?')) return;
    try {
      await axios.delete(`${API}/admin/llm-artifacts/${userId}`);
      toast.success('Snapshot purgé');
      setSnapshots((s) => s.filter((x) => x.user_id !== userId));
    } catch (err) {
      toast.error('Échec de la suppression');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8" data-testid="llm-artifacts-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="text-slate-400 hover:text-sky-400 mb-2"
              data-testid="btn-back-profile"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour au profil
            </Button>
            <h1 className="text-3xl md:text-4xl font-orbitron uppercase tracking-wider text-amber-400 flex items-center">
              <FileWarning className="w-7 h-7 md:w-8 md:h-8 mr-3" />
              Artefacts LLM
            </h1>
            <p className="text-sm text-slate-500 mt-2 max-w-3xl">
              Snapshots des générations contenant des artefacts JSON/HTML résiduels.
              Un seul snapshot par utilisateur (écrasement automatique).
              Auto-purgé 7 jours après la dernière erreur.
            </p>
          </div>
          <Button
            onClick={load}
            disabled={loading}
            className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/50"
            data-testid="btn-refresh-snapshots"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {loading && (
          <Card className="bg-slate-950/40 border-slate-800/60">
            <CardContent className="py-12 text-center text-slate-500">
              <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-sky-400" />
              Chargement...
            </CardContent>
          </Card>
        )}

        {!loading && snapshots.length === 0 && (
          <Card className="bg-slate-950/40 border-emerald-800/40" data-testid="no-snapshots">
            <CardContent className="py-12 text-center">
              <div className="text-emerald-400 text-lg font-orbitron uppercase tracking-wider mb-2">
                Aucun artefact détecté
              </div>
              <p className="text-sm text-slate-500">
                Toutes les générations récentes ont produit des articles propres.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && snapshots.length > 0 && (
          <div className="space-y-4">
            {snapshots.map((snap) => {
              const isOpen = !!expanded[snap.user_id];
              const det = detail[snap.user_id];
              return (
                <Card
                  key={snap.user_id}
                  className="bg-slate-950/60 border-amber-800/40 hover:border-amber-700/60 transition-colors"
                  data-testid={`snapshot-card-${snap.user_id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => toggleExpand(snap.user_id)}
                          className="flex items-center gap-2 text-left w-full"
                          data-testid={`btn-expand-${snap.user_id}`}
                        >
                          {isOpen ? <ChevronDown className="w-5 h-5 text-amber-400 flex-shrink-0" /> : <ChevronRight className="w-5 h-5 text-amber-400 flex-shrink-0" />}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <UserIcon className="w-4 h-4 text-sky-400" />
                              <span className="font-orbitron text-sky-400 uppercase tracking-wider text-sm">
                                {snap.user_name}
                              </span>
                              <Badge variant="outline" className="text-slate-400 border-slate-700">
                                {snap.user_email}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-2 flex-wrap text-xs">
                              <span className="flex items-center gap-1 text-slate-500">
                                <Clock className="w-3 h-3" />
                                {formatDate(snap.updated_at)}
                              </span>
                              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40">
                                {snap.dirty_articles}/{snap.total_articles} articles sales
                              </Badge>
                              {snap.all_violations?.slice(0, 3).map((v, i) => (
                                <Badge key={i} variant="outline" className="text-red-400 border-red-800/60 text-[10px]">
                                  {v}
                                </Badge>
                              ))}
                              {snap.all_violations?.length > 3 && (
                                <span className="text-xs text-slate-600">+{snap.all_violations.length - 3}</span>
                              )}
                            </div>
                          </div>
                        </button>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(snap.user_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`btn-delete-${snap.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>

                  {isOpen && (
                    <CardContent className="pt-0 border-t border-slate-800/60">
                      {!det && (
                        <div className="py-6 text-center text-slate-500">
                          <RefreshCw className="w-5 h-5 mx-auto animate-spin" />
                        </div>
                      )}
                      {det && (
                        <div className="space-y-4 mt-4" data-testid={`detail-${snap.user_id}`}>
                          <div>
                            <h3 className="text-xs uppercase tracking-wider text-sky-400 mb-2 font-semibold">
                              Paramètres du job
                            </h3>
                            <pre className="bg-slate-900/60 border border-slate-800 rounded p-3 text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap break-words max-h-72">
                              {JSON.stringify(det.job_params, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <h3 className="text-xs uppercase tracking-wider text-sky-400 mb-2 font-semibold">
                              Articles générés ({det.articles?.length || 0})
                            </h3>
                            <div className="space-y-3">
                              {(det.articles || []).map((a, i) => (
                                <div
                                  key={i}
                                  className={`rounded p-3 border ${
                                    a.violations?.length
                                      ? 'bg-red-950/30 border-red-900/40'
                                      : 'bg-emerald-950/20 border-emerald-900/40'
                                  }`}
                                  data-testid={`article-${i}`}
                                >
                                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                    <div className="font-semibold text-slate-100 text-sm">
                                      {a.title || '(sans titre)'}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {a.site_name && (
                                        <Badge variant="outline" className="text-sky-400 border-sky-800/60 text-[10px]">
                                          {a.site_name}
                                        </Badge>
                                      )}
                                      <Badge variant="outline" className="text-slate-500 border-slate-700 text-[10px]">
                                        {a.content_length} chars
                                      </Badge>
                                      {a.seo_score != null && (
                                        <Badge variant="outline" className="text-slate-500 border-slate-700 text-[10px]">
                                          SEO {a.seo_score}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  {a.violations?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2">
                                      {a.violations.map((v, vi) => (
                                        <Badge key={vi} className="bg-red-500/20 text-red-400 border-red-500/40 text-[10px]">
                                          <AlertTriangle className="w-3 h-3 mr-1" />
                                          {v}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                  <details className="text-xs">
                                    <summary className="cursor-pointer text-slate-400 hover:text-slate-200">
                                      Voir le contenu (extrait)
                                    </summary>
                                    <pre className="mt-2 bg-slate-950/60 border border-slate-800 rounded p-2 text-[11px] text-slate-400 overflow-x-auto whitespace-pre-wrap break-words max-h-60">
                                      {a.content_preview}
                                      {a.content_tail && '\n\n... [fin] ...\n\n' + a.content_tail}
                                    </pre>
                                  </details>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LLMArtifacts;
